import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const [activeService, setActiveService] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: 1,
      icon: 'ri-building-line',
      title: 'Business Registration',
      subtitle: 'LLC, Ltd, Corporation, Nonprofit',
      description: 'Complete business formation services including LLC, Limited Company, Corporation, and Nonprofit registration. We handle all paperwork and compliance requirements to get your business legally established.',
      features: ['Entity Formation', 'Articles of Incorporation', 'Operating Agreements', 'Registered Agent Services'],
    },
    {
      id: 2,
      icon: 'ri-edit-line',
      title: 'Business Amendments & Closures',
      subtitle: 'Modifications and Dissolution Services',
      description: 'Professional assistance with business structure changes, amendments to articles of incorporation, and proper business dissolution procedures when needed.',
      features: ['Name Changes', 'Address Updates', 'Ownership Transfers', 'Business Dissolution'],
    },
    {
      id: 3,
      icon: 'ri-file-text-line',
      title: 'EIN/TIN/Tax ID Application',
      subtitle: 'Federal Tax Identification',
      description: 'Fast and accurate processing of Employer Identification Numbers (EIN), Tax Identification Numbers (TIN), and related tax registration services.',
      features: ['EIN Registration', 'TIN Processing', 'Tax Account Setup', 'IRS Correspondence'],
    },
    {
      id: 4,
      icon: 'ri-computer-line',
      title: 'Website Design',
      subtitle: 'Wix, WordPress, GoDaddy, Shopify',
      description: 'Professional website development on popular platforms. From simple business websites to complex e-commerce stores, we create digital solutions that drive results.',
      features: ['Custom Design', 'E-commerce Setup', 'SEO Optimization', 'Mobile Responsive'],
    },
    {
      id: 5,
      icon: 'ri-calculator-line',
      title: 'Tax Filing & VAT Registration',
      subtitle: 'Compliance and Tax Services',
      description: 'Comprehensive tax preparation and filing services, including VAT registration for international businesses and ongoing tax compliance support.',
      features: ['Tax Preparation', 'VAT Registration', 'Quarterly Filings', 'Tax Planning'],
    },
    {
      id: 6,
      icon: 'ri-government-line',
      title: 'Government Contracting',
      subtitle: 'SAM, CAGE, UEI, Proposals',
      description: 'Complete government contracting services including SAM.gov registration, CAGE code acquisition, UEI registration, and professional proposal writing.',
      features: ['SAM Registration', 'CAGE Code', 'UEI Processing', 'Capability Statements'],
    },
    {
      id: 7,
      icon: 'ri-hand-coin-line',
      title: 'Grant Writing',
      subtitle: 'US, UK, Canada Funding',
      description: 'Expert grant writing services for businesses and nonprofits seeking funding opportunities in the United States, United Kingdom, and Canada.',
      features: ['Grant Research', 'Proposal Writing', 'Budget Development', 'Submission Support'],
    },
    {
      id: 8,
      icon: 'ri-auction-line',
      title: 'Tender Applications',
      subtitle: 'Bidding Support Services',
      description: 'Professional tender application preparation and bidding support to help you win government and corporate contracts.',
      features: ['Tender Research', 'Bid Preparation', 'Compliance Review', 'Submission Management'],
    },
    {
      id: 9,
      icon: 'ri-presentation-line',
      title: 'Pitch Decks & Business Plans',
      subtitle: 'Strategic Planning Documents',
      description: 'Compelling pitch decks and comprehensive business plans that attract investors and guide your business growth strategy.',
      features: ['Investor Presentations', 'Business Strategy', 'Financial Projections', 'Market Analysis'],
    },
    {
      id: 10,
      icon: 'ri-file-list-line',
      title: 'Policy & Procedures',
      subtitle: 'Organizational Documentation',
      description: 'Development of professional policies, procedures, and operational guidelines to ensure compliance and efficiency in your organization.',
      features: ['HR Policies', 'Operational Procedures', 'Compliance Documentation', 'Training Materials'],
    },
    {
      id: 11,
      icon: 'ri-file-user-line',
      title: 'Resume & Career Support',
      subtitle: 'Professional Development',
      description: 'Professional resume writing and career development services to help you advance your career or support your team members.',
      features: ['Resume Writing', 'LinkedIn Optimization', 'Interview Coaching', 'Career Planning'],
    },
    {
      id: 12,
      icon: 'ri-shopping-cart-line',
      title: 'E-commerce Consulting',
      subtitle: 'Online Store Development',
      description: 'Complete e-commerce solutions including store setup, payment processing, inventory management, and digital marketing strategies.',
      features: ['Store Setup', 'Payment Integration', 'Inventory Management', 'Marketing Strategy'],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-['Poppins']">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed mb-8">
              Comprehensive consulting solutions for business owners and nonprofit organizations worldwide.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm lg:text-base">
              <span className="bg-white/20 px-4 py-2 rounded-full">Business Registration</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Government Contracts</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Grant Writing</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Web Design & More</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">What We Offer</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From business formation to advanced consulting, we provide end-to-end solutions to help you succeed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div 
                  key={service.id}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                  onClick={() => setActiveService(activeService === service.id ? null : service.id)}
                >
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-black mb-2">{service.title}</h3>
                  <p className="text-red-600 font-semibold mb-4">{service.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  
                  {activeService === service.id && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold text-black mb-3">What's Included:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <i className="ri-check-line text-red-600 mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/contact');
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 whitespace-nowrap"
                  >
                    Learn More & Book
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">How We Work</h2>
              <p className="text-xl text-gray-600">Our streamlined process ensures fast, professional results</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">Consultation</h3>
                <p className="text-gray-600">Free initial consultation to understand your needs and goals</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">Strategy</h3>
                <p className="text-gray-600">Develop customized strategy and timeline for your project</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">Execution</h3>
                <p className="text-gray-600">Professional execution with regular updates and communication</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">Delivery</h3>
                <p className="text-gray-600">Fast delivery with ongoing support and follow-up services</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Assistance Banner */}
      <section className="py-16 bg-gradient-to-r from-black to-red-600 text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-4">
              <i className="ri-computer-line text-4xl mr-4"></i>
              <h2 className="text-2xl lg:text-3xl font-bold">Website Assistance Available</h2>
            </div>
            <p className="text-lg opacity-90 mb-6">
              Need help building or improving your website? Our team specializes in creating professional websites 
              that drive results for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                href="https://www.fiverr.com/abiodun136"
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-semibold whitespace-nowrap"
              >
                Get Website Help
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 px-6 py-3 font-semibold whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Chat About Your Project
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Click below to schedule your consultation and take the first step towards achieving your business goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Schedule Consultation
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-black hover:bg-gray-800 text-white px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Services;